<template>
  <div
    ref="mapContainer"
    :aria-label="$t('storeLocator.mapAriaLabel')"
    role="application"
    data-testid="store-locator-map"
    class="size-full min-h-96 rounded-md bg-neutral-200"
  />
</template>

<script setup lang="ts">
import type { StoreLocatorMapEmits, StoreLocatorMapProps } from '~/components/StoreLocator/types';

const props = defineProps<StoreLocatorMapProps>();
const emit = defineEmits<StoreLocatorMapEmits>();

const { t } = useI18n();
const runtimeConfig = useRuntimeConfig();
const mapContainer = ref<HTMLElement | null>(null);

const gwin = (): Window & { google?: typeof google; initGoogleMaps?: () => void } =>
  window as Window & { google?: typeof google; initGoogleMaps?: () => void };

let map: google.maps.Map | null = null;
const markerMap = new Map<string, google.maps.Marker>();
let userMarker: google.maps.Marker | null = null;
let userInfoWindow: google.maps.InfoWindow | null = null;

const createPinIcon = (active: boolean): google.maps.Icon => {
  const fill = active ? '#232C20' : '#6A8560';
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="36" viewBox="0 0 28 36">
    <path d="M14 0C6.268 0 0 6.268 0 14c0 9.333 14 22 14 22S28 23.333 28 14C28 6.268 21.732 0 14 0z" fill="${fill}"/>
    <circle cx="14" cy="13" r="5" fill="white"/>
  </svg>`;
  return {
    anchor: new google.maps.Point(14, 36),
    scaledSize: new google.maps.Size(28, 36),
    url: 'data:image/svg+xml,' + encodeURIComponent(svg),
  };
};

const createUserPinIcon = (): google.maps.Icon => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="36" viewBox="0 0 28 36">
    <path d="M14 0C6.268 0 0 6.268 0 14c0 9.333 14 22 14 22S28 23.333 28 14C28 6.268 21.732 0 14 0z" fill="#22c55e"/>
    <circle cx="14" cy="13" r="5" fill="white"/>
  </svg>`;
  return {
    anchor: new google.maps.Point(14, 36),
    scaledSize: new google.maps.Size(28, 36),
    url: 'data:image/svg+xml,' + encodeURIComponent(svg),
  };
};

const placeUserMarker = (position: { lat: number; lng: number }): void => {
  if (!map) return;

  userInfoWindow?.close();
  userMarker?.setMap(null);

  userMarker = new google.maps.Marker({
    icon: createUserPinIcon(),
    map,
    position,
  });

  userInfoWindow = new google.maps.InfoWindow({ content: t('storeLocator.youAreHere') });
  userInfoWindow.open(map, userMarker);

  map.panTo(position);
};

const initMap = (): void => {
  if (!mapContainer.value || !gwin().google?.maps) return;

  const storesWithCoords = props.stores.filter((s) => s.latitude !== undefined && s.longitude !== undefined);

  const firstStore = storesWithCoords[0];
  const center =
    firstStore !== undefined
      ? { lat: firstStore.latitude!, lng: firstStore.longitude! }
      : { lat: 46.603354, lng: 1.888334 };

  map = new google.maps.Map(mapContainer.value, {
    center,
    zoom: 12,
  });

  placeMarkers();

  if (props.userPosition) placeUserMarker(props.userPosition);
};

const placeMarkers = (): void => {
  if (!map) return;

  markerMap.forEach((marker) => marker.setMap(null));
  markerMap.clear();

  for (const store of props.stores) {
    if (store.latitude === undefined || store.longitude === undefined) continue;

    const marker = new google.maps.Marker({
      icon: createPinIcon(store.id === props.activeStoreId),
      map,
      position: { lat: store.latitude, lng: store.longitude },
      title: store.name,
    });

    marker.addListener('click', () => {
      emit('select', store.id);
    });

    markerMap.set(store.id, marker);
  }
};

const loadGoogleMaps = (): void => {
  const apiKey = runtimeConfig.public.googleMapsApiKey;
  if (!apiKey || gwin().google?.maps) {
    initMap();
    return;
  }

  gwin().initGoogleMaps = initMap;
  const script = document.createElement('script');
  script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&callback=initGoogleMaps`;
  script.async = true;
  script.defer = true;
  document.head.appendChild(script);
};

onMounted(() => {
  loadGoogleMaps();
});

watch(
  () => props.stores,
  () => {
    if (map) placeMarkers();
  },
);

watch(
  () => props.activeStoreId,
  (newId, oldId) => {
    if (!map) return;
    if (oldId) {
      markerMap.get(oldId)?.setIcon(createPinIcon(false));
    }
    if (newId) {
      const marker = markerMap.get(newId);
      if (marker) {
        marker.setIcon(createPinIcon(true));
        map.panTo(marker.getPosition()!);
      }
    }
  },
);

watch(
  () => props.userPosition,
  (pos) => {
    if (pos && map) placeUserMarker(pos);
  },
);

onUnmounted(() => {
  userInfoWindow?.close();
  userMarker?.setMap(null);
});
</script>
